// src/Pages/Home.js
import React, { useState } from "react";
import DashboardCard from "../../Components/DashboardCard";
import {
  FaMoneyBillWave,
  FaUser,
  FaDollarSign,
  FaHeartbeat,
  FaChartBar,
} from "react-icons/fa";
import WalletCard from "../Wallet/WalletCard";
import useQuickStats from "../../Hooks/Dashboard/useQuickStats";
import useTrending from "../../Hooks/Dashboard/useTrendingInsights";
import usePerformance from "../../Hooks/Dashboard/usePerformance";
import { translations, iconsMap } from "../../Data/homeConstants";
import SideCard from "../../Components/SideCard";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  LineChart,
  Line,
  CartesianGrid,
} from "recharts";
import ChartCard from "../../Components/ChartCard";
import CustomDropdown from "../../Components/CustomDropdown";

const Home = () => {
  const [period, setPeriod] = useState("24h");

  const { stats, generatedAt, error, isLoading, reload } = useQuickStats({ period });
  const { insights, error: trendingError, isLoading: trendingLoading, reload: reloadTrending } =
    useTrending({ period });

  const mostActiveUser = insights.find((i) => i.title === "Most Active User");
  const highestTransaction = insights.find((i) => i.title === "Highest Transaction");

  const {
    metrics,
    error: perfError,
    isLoading: perfLoading,
  } = usePerformance({ period, include_hourly: true });

  return (
    <div className="p-6 space-y-6 font-cairo dir-rtl ">

      <div className="bg-grayish border rounded-xl shadow-md p-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">

        <h6 className="text-lg font-semibold text-gray-700 sm:order-1">
          الصفحة الرئيسية
        </h6>


        <div className="flex flex-col items-end gap-2 sm:order-2">
          <CustomDropdown
            options={[
              { value: "24h", label: "آخر 24 ساعة" },
              { value: "7d", label: "آخر 7 أيام" },
              { value: "30d", label: "آخر 30 يوم" },
              { value: "90d", label: "آخر 90 يوم" },
              { value: "1y", label: "آخر سنة" },
            ]}
            selectedValue={period}
            onChange={setPeriod}
          />

        </div>
      </div>

      {/* 🔹 الكروت العلوية */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <WalletCard index={0} />

        {isLoading ? (
          <p>جاري تحميل الإحصائيات...</p>
        ) : error ? (
          <div className="text-red-500">
            <p>{error.message || "حدث خطأ ما"}</p>
            <button onClick={reload} className="underline text-sm">
              إعادة المحاولة
            </button>
          </div>
        ) : (
          stats.map((stat, i) => (
            <DashboardCard
              key={stat.key}
              icon={iconsMap[stat.key] || <FaMoneyBillWave />}
              title={translations[stat.key] || stat.label}
              value={stat.value.toLocaleString()}
              lastUpdate={generatedAt}
              index={i + 1}
            />
          ))
        )}
      </div>

      {/* 🔹 قسم مؤشرات الأداء */}
      <div>
        <h2 className="text-lg font-semibold text-gray-700 mb-4">
          الإحصائيات المتقدمة ومؤشرات الأداء
        </h2>

        {perfLoading ? (
          <p>جاري تحميل مؤشرات الأداء...</p>
        ) : perfError ? (
          <div className="text-red-500">
            <p>{perfError.message || "حدث خطأ ما"}</p>
            <button
              onClick={() => window.location.reload()}
              className="underline text-sm"
            >
              إعادة المحاولة
            </button>
          </div>
        ) : (
          metrics && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 items-stretch">
              {/* العمود الأول: صحة النظام + الإحصاءات المتقدمة */}
              <div className="flex flex-col gap-3 h-full">
                <SideCard title="صحة النظام" icon={<FaHeartbeat />}>
                  <div className="h-full flex flex-col justify-center">
                    <p className="text-2xl font-bold text-main1">
                      {metrics.system_health.overall_success_rate.toFixed(1)}%
                    </p>
                    <p className="text-sm text-gray-500">
                      إجمالي العمليات: {metrics.system_health.total_processed}
                    </p>
                  </div>
                </SideCard>

                {trendingLoading ? (
                  <p>جاري تحميل الإحصاءات المتقدمة...</p>
                ) : trendingError ? (
                  <div className="text-red-500">
                    <p>{trendingError.message || "حدث خطأ ما"}</p>
                    <button
                      onClick={reloadTrending}
                      className="underline text-sm"
                    >
                      إعادة المحاولة
                    </button>
                  </div>
                ) : (
                  <>
                    {mostActiveUser && (
                      <SideCard title="أكثر مستخدم نشاطاً" icon={<FaUser />}>
                        <div className="h-full flex flex-col justify-center">
                          <p className="font-semibold text-main1">
                            {mostActiveUser.description}
                          </p>
                          <p className="text-sm text-gray-500">
                            عدد المعاملات: {mostActiveUser.value}
                          </p>
                        </div>
                      </SideCard>
                    )}

                    {highestTransaction && (
                      <SideCard title="أكبر معاملة" icon={<FaDollarSign />}>
                        <div className="h-full flex flex-col justify-center">
                          <p className="font-semibold text-main1 text-md">
                            {highestTransaction.value.toLocaleString()}
                          </p>
                          <p className="text-sm text-gray-500">
                            {highestTransaction.description}
                          </p>
                        </div>
                      </SideCard>
                    )}
                  </>
                )}
              </div>

              {/* العمود الثاني: نسب نجاح الشبكات */}
              <ChartCard title="إحصائيات المزودين" icon={<FaChartBar />}>
                <div className="h-full flex flex-col justify-center">
                  <ResponsiveContainer width="100%" height={275}>
                    <BarChart
                      data={Object.keys(metrics.success_rates).map((op) => ({
                        name: op.toUpperCase(),
                        success: metrics.success_rates[op].success_rate,
                        failure: metrics.success_rates[op].failure_rate,
                      }))}
                    >
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar
                        dataKey="success"
                        fill="#1C4130"
                        name="نجاح %"
                        radius={[6, 6, 0, 0]}
                        barSize={40}
                      />
                      <Bar
                        dataKey="failure"
                        fill="#C03F2A"
                        name="فشل %"
                        radius={[6, 6, 0, 0]}
                        barSize={40}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </ChartCard>

              {/* العمود الثالث: توزيع الاستخدام بالساعات */}
              <ChartCard title="توزيع الاستخدام بالساعات">
                <div className="h-full flex flex-col justify-center">
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={metrics.usage_patterns.hourly_distribution}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" />
                      <YAxis />
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey="transactions"
                        stroke="#1C4130"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                  <p className="text-sm text-gray-500 mt-2">
                    ساعة الذروة: {metrics.usage_patterns.peak_hour}:00
                  </p>
                </div>
              </ChartCard>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default Home;
